/**
 * @file Shared layout values for the invitations list screens.
 * @module features/invitations/constants
 */
import type { ViewStyle } from "react-native";

/**
 * Builds the `contentContainerStyle` for an invitations `FlashList`.
 *
 * Each invitations list pins its panel to a fixed width and expects that panel
 * to be centred in the content area and to fill the available height. Those two
 * rules used to be written as `contentContainerClassName="flex-grow … self-center"`,
 * and they never took effect. NativeWind only rewrites `*ClassName` props for
 * components it has registered, and `react-native-css-interop`'s
 * `runtime/components.ts` registers `ScrollView`, `FlatList` and
 * `VirtualizedList` — nothing registers `@shopify/flash-list`. The class string
 * was handed straight to FlashList, which does not read it.
 *
 * Measured on staging web at 1440x742 (SE-6281): the content container computed
 * `align-self: auto`, `flex-grow: 0` and `padding: 0px`, while the `width: 640px`
 * coming from `contentContainerStyle` WAS applied. So the panel sat flush left
 * at x=64 inside a 1376px parent and collapsed to its content height. That is
 * what put the "No Invitations" empty state in the top-left corner:
 * `ListEmptyView` centres inside `h-4/5`, and 80% of a collapsed container
 * leaves nothing to centre within.
 *
 * `contentContainerStyle` is the channel FlashList honours, so the two layout
 * rules live here instead. The `p-3`/`pb-4` padding from the old class strings
 * is deliberately NOT reinstated — it has never rendered on any platform, so
 * introducing it now would be a separate visual change rather than a fix.
 * @param width - Fixed panel width for the current viewport.
 * @returns Style centring the list panel and letting it fill the viewport.
 */
export const invitationsListContentContainerStyle = (
  width: number
): ViewStyle => ({
  alignSelf: "center",
  flexGrow: 1,
  width,
});
